<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6">
        {{ $t("acceptance.pra.request.dialog.title") }}
      </v-card-title>
      <v-card-subtitle class="pra-wrap">
        {{ subtitle }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.date"
              type="date"
              variant="outlined"
              density="comfortable"
              :min="limits.min"
              :max="limits.max"
              :label="`${$t('acceptance.pra.request.date')} *`"
              :hint="dateHint"
              persistent-hint
              :error-messages="message('date')"
              :disabled="saving"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.channel"
              :items="channelItems"
              variant="outlined"
              density="comfortable"
              :label="`${$t('acceptance.pra.request.channel')} *`"
              :disabled="saving"
            />
          </v-col>
          <v-col v-if="form.channel === 'sta_agency'" cols="12" sm="6">
            <v-text-field
              v-model="form.agencyName"
              variant="outlined"
              density="comfortable"
              :maxlength="agencyMax"
              :label="`${$t('acceptance.pra.request.agency_name')} *`"
              :error-messages="message('agencyName')"
              :disabled="saving"
            />
          </v-col>
        </v-row>

        <div class="text-body-2 mt-4 mb-1">
          {{ $t("acceptance.pra.request.dialog.file") }}
        </div>
        <div class="pra-file-box d-flex flex-wrap align-center ga-2 pa-3">
          <v-chip
            v-if="form.file"
            size="small"
            variant="tonal"
            color="primary"
            closable
            :disabled="saving"
            @click:close="form.file = null"
          >
            {{ form.file.name }} · {{ fileSize }}
          </v-chip>
          <span v-else class="text-body-2">
            {{ $t("acceptance.pra.request.dialog.no_file") }}
          </span>
          <v-btn
            size="small"
            variant="outlined"
            color="primary"
            :disabled="saving"
            @click="pickFile"
          >
            {{
              form.file
                ? $t("acceptance.pra.crd.dialog.change_file")
                : $t("acceptance.pra.crd.dialog.choose_file")
            }}
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            :accept="accept"
            @change="onFile"
          />
        </div>
        <div
          class="text-caption mt-1"
          :class="fileProblem ? 'text-error' : 'text-grey-darken-1'"
        >
          {{ fileProblem || $t("acceptance.pra.request.dialog.file_hint") }}
        </div>

        <!-- Lateness never blocks the save: the request is recorded as late. -->
        <v-alert
          v-if="late"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{
            $t("acceptance.pra.request.dialog.late_warning", {
              date: dueDateText,
            })
          }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("acceptance.pra.crd.dialog.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="submit"
        >
          {{ $t("acceptance.pra.request.dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatCivilDate } from "@/components/Acceptance/deadlines.mjs";
import { FILE_TYPES } from "@/components/Acceptance/dossierFiles.mjs";
import { formatFileSize } from "@/components/Acceptance/praCrdForm.mjs";
import {
  AGENCY_NAME_MAX,
  REQUEST_CHANNELS,
  emptyRequestForm,
  isRequestLate,
  requestDateLimits,
  requestFormErrors,
  requestPayload,
} from "@/components/Acceptance/praRequestForm.mjs";

// "Registra richiesta di radiazione" (Stage 2 slice 6): date, channel (with the agency name for
// Agenzia STA) and an optional file. A live amber note shows when the date is after the
// due date. The page sends it through the case session; a save error keeps the dialog open with
// the input and the server message.
export default {
  name: "AcceptancePraRequestDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    // Save failure from the page, shown inside the dialog; the input is kept.
    error: { type: String, default: "" },
    // "{vehicle} · {plate}" of the acceptance
    vehicle: { type: String, default: "" },
    // The CRD date issued (YYYY-MM-DD): the earliest request date.
    crdDate: { type: String, default: "" },
    // The case's deregistration-request due date (YYYY-MM-DD), for the live late note.
    dueDate: { type: String, default: "" },
  },
  emits: ["update:modelValue", "submit"],
  data: () => ({
    form: emptyRequestForm(),
    now: new Date(),
  }),
  computed: {
    agencyMax() {
      return AGENCY_NAME_MAX;
    },
    accept() {
      return FILE_TYPES.join(",");
    },
    channelItems() {
      return REQUEST_CHANNELS.map((value) => ({
        value,
        title: this.$t(`acceptance.pra.request.channels.${value}`),
      }));
    },
    limits() {
      return requestDateLimits({ crdDate: this.crdDate, now: this.now });
    },
    errors() {
      return requestFormErrors(this.form, this.limits);
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
    late() {
      return !this.errors.date && isRequestLate(this.form.date, this.dueDate);
    },
    dueDateText() {
      return formatCivilDate(this.dueDate) || "—";
    },
    subtitle() {
      return [this.vehicle, this.$t("acceptance.pra.source_manual")]
        .filter(Boolean)
        .join(" · ");
    },
    dateHint() {
      return this.$t("acceptance.pra.request.dialog.date_hint", {
        date: formatCivilDate(this.limits.min) || "—",
      });
    },
    fileSize() {
      return this.form.file ? formatFileSize(this.form.file.size) : "";
    },
    fileProblem() {
      const reason = this.errors.file;
      if (!reason) return "";
      return this.$t(`acceptance.files.errors.${reason}`);
    },
  },
  watch: {
    modelValue(open) {
      if (!open) return;
      this.now = new Date();
      this.form = emptyRequestForm();
    },
  },
  methods: {
    // An empty required field only disables the save; other problems show under the field.
    message(field) {
      const reason = this.errors[field];
      if (!reason || reason === "required") return [];
      return [
        this.$t(`acceptance.pra.request.dialog.errors.${reason}`, {
          max: AGENCY_NAME_MAX,
        }),
      ];
    },
    pickFile() {
      this.$refs.fileInput.click();
    },
    onFile(event) {
      const [file] = event.target.files || [];
      if (file) this.form.file = file;
      // The same file can be picked again after a change.
      event.target.value = ""; // eslint-disable-line no-param-reassign
    },
    submit() {
      this.now = new Date();
      if (!this.valid || this.saving) return;
      this.$emit("submit", requestPayload(this.form));
    },
  },
};
</script>

<style scoped>
.pra-wrap {
  white-space: normal;
}
.pra-file-box {
  border: 1px dashed rgba(0, 0, 0, 0.24);
  border-radius: 4px;
}
</style>
